import { Fragment, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import BrowseAllResultsFooter from '../BrowseAllResultsFooter'
import DrawerHotelTile from './DrawerHotelTile'
import DrawerHotelTileSkeleton from './DrawerHotelTileSkeleton'
import HotelListingEmptyState from './HotelListingEmptyState'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelDetailView'
import type { HotelListingDrawerProps } from './types'
import { formatDrawerHotelInfo } from './formatDrawerHotelInfo'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import { isOfferBookedInItinerary } from 'tripPlanner/utils/chatItinerary'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import ListingDrawerSearchControls from '../ListingDrawerSearchControls'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useHotelListingDrawerData, {
  HOTEL_DRAWER_MAX_GUESTS,
  HOTEL_DRAWER_MAX_WIDTH_PX,
} from './useHotelListingDrawerData'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'
import { formatTripDrawerMapLabel } from '../formatTripDrawerMapLabel'
import { buildHotelSearchDrawerContext } from '../../utils/drawerAskAIContext'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SKELETON_COUNT = 3

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const DETAIL_CONTENT_WIDTH = 1188

export const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: 100%;
  max-width: ${rem(DETAIL_CONTENT_WIDTH)};
  min-width: 0;
  box-sizing: border-box;
`

function HotelListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopStartDate,
  stopEndDate,
  stopCoordinates,
  contextStop,
  onShowMap,
  onExploreMap,
  itineraryData,
  onSendMessage,
  initialSelectedOfferId,
  initialSelectedOfferType,
}: HotelListingDrawerProps) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const {
    browseAllResultsUrl,
    canGoBack,
    canShowExploreSection,
    checkIn,
    checkOut,
    dayRangeLabel,
    detailHotel,
    exploreHeading,
    filterNights,
    handleAfterClose,
    handleBack,
    handleClose,
    handleSelectHotel,
    hasHotels,
    hasMore,
    hasSavedHotels,
    headerTitle,
    isBooked,
    isDetailError,
    isDetailLoading,
    isDetailView,
    isLoading,
    isLoadingMore,
    loadMore,
    loadMoreTriggerRef,
    occupancies,
    orderIdForLink,
    refetchDetail,
    reviews,
    reviewsLoading,
    savedBookedRooms,
    savedBooking,
    savedDrawerHotels,
    savedHeading,
    selectedHotelTitle,
    searchLocation,
    setOccupancies,
    setSearchLocation,
    setSelectedCheckIn,
    setSelectedCheckOut,
    setSelectedSearchItem,
    orderedHotelsWithTileDetails,
    orderedHotelSearchSections,
    tracking,
    trackingOfferById,
  } = useHotelListingDrawerData({
    isOpen,
    onClose,
    placeName,
    placeCountry,
    stopStartDate,
    stopEndDate,
    stopCoordinates,
    contextStop,
    itineraryData,
    initialSelectedOfferId,
    initialSelectedOfferType,
  })

  const handleShowMapFromHeader = useCallback(() => {
    if (detailHotel && onShowMap) {
      onShowMap(detailHotel)
    }
  }, [onShowMap, detailHotel])

  const listHeaderTitle =
    headerTitle ??
    intl.formatMessage(
      {
        id: 'G-nNmBr53dHAL8cBtSNe',
        defaultMessage: 'Hotels in {placeName}',
        description:
          'Heading for hotel listing drawer showing hotels in a specific place',
      },
      {
        placeName: formatPlacePhrase({
          location: { name: placeName },
          language: intl.locale,
          intent: 'location',
        }),
      },
    )

  const shouldRenderTitleInContent =
    shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const shouldRenderDetailTitleInContent =
    isDetailView && shouldRenderTitleInContent
  const shouldRenderListTitleInContent =
    !isDetailView && shouldRenderTitleInContent
  const hotelSearchSectionByPosition = useMemo(() => {
    return new Map(orderedHotelSearchSections.map((section) => [section.position, section]))
  }, [orderedHotelSearchSections])
  const nearbyOffersHeading = intl.formatMessage({
    id: '-DXsf5HRceqn854D3Yad',
    defaultMessage: 'Nearby offers',
    description:
      'Section heading in the trip planner hotel drawer before hotel offers near but outside the exact searched destination.',
  })

  const drawerHeader = useMemo(() => {
    if (shouldRenderDetailTitleInContent || shouldRenderListTitleInContent) return undefined

    const title = isDetailView ? selectedHotelTitle : listHeaderTitle
    if (!title) return undefined

    return <TripDrawerHeaderTitle title={title} />
  }, [isDetailView, listHeaderTitle, selectedHotelTitle, shouldRenderDetailTitleInContent, shouldRenderListTitleInContent])

  const listHeaderActions = useMemo(() => {
    if (isDetailView) return undefined

    if (isMobileScreen) {
      return (
        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="end" wrap="wrap">
          {hasHotels && (
            <FilterChip
              size="small"
              href={browseAllResultsUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<LineExternalLinkAltIcon />}
            >
              {intl.formatMessage({
                id: 'itl8WEctxs1Z0nf23fD3',
                defaultMessage: 'Browse all results',
                description:
                  'Button label linking to the full hotel search results page',
              })}
            </FilterChip>
          )}
          {onExploreMap && (
            <FilterChip
              size="small"
              startIcon={<LineMapIcon />}
              onClick={onExploreMap}
            >
              {intl.formatMessage({
                id: '9riZb6FinKgw_bgJCPsX',
                defaultMessage: 'Map view',
                description:
                  'Mobile chip label on the trip planner hotel listing drawer header that opens map explore for hotels at the current itinerary stop.',
              })}
            </FilterChip>
          )}
        </Group>
      )
    }

    if (!onExploreMap) return undefined

    return (
      <FilterChip
        size="small"
        startIcon={<LineMapIcon />}
        onClick={onExploreMap}
      >
        {intl.formatMessage({
          id: 'jcH-AcKGdZjLXZnnZGK9',
          defaultMessage: 'Explore more',
          description:
            'Chip label on the trip planner hotel listing drawer header that opens map explore for hotels at the current itinerary stop.',
        })}
      </FilterChip>
    )
  }, [browseAllResultsUrl, hasHotels, intl, isDetailView, isMobileScreen, onExploreMap])

  const detailHeaderActions = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    const hasGeo = !!detailHotel.geoLocation
    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeo && onShowMap && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMapFromHeader}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dayRangeLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">{dayRangeLabel}</FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={detailHotel.name}
            offerId={detailHotel.offerId}
            offerType={detailHotel.offerType}
            hotelCity={detailHotel.location}
            hotelCoordinates={detailHotel.geoLocation}
            contextStop={contextStop}
            onSendMessage={onSendMessage}
            onAfterAction={handleClose}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [
    isDetailView,
    detailHotel,
    drawerPresentation,
    handleShowMapFromHeader,
    dayRangeLabel,
    intl,
    onSendMessage,
    isBooked,
    itineraryData,
    contextStop,
    handleClose,
    onShowMap,
  ])

  const browseAllResultsFooter = useMemo(() => {
    if (isDetailView || isMobileScreen || !hasHotels) return undefined

    return (
      <BrowseAllResultsFooter
        heading={intl.formatMessage({
          id: 'QFGNPlPZs7ycwnBOsTQA',
          defaultMessage: 'There’s plenty more to explore',
          description:
            'Prompt heading inviting users to browse all hotel search results',
        })}
        body={intl.formatMessage({
          id: 'YC1EB2OcYdrQD1ci1sXS',
          defaultMessage: 'Browse all available hotels for this destination.',
          description:
            'Supporting text encouraging users to view more hotel search results',
        })}
        browseAllResultsUrl={browseAllResultsUrl}
        buttonLabel={intl.formatMessage({
          id: 'itl8WEctxs1Z0nf23fD3',
          defaultMessage: 'Browse all results',
          description:
            'Button label linking to the full hotel search results page',
        })}
      />
    )
  }, [browseAllResultsUrl, hasHotels, intl, isDetailView, isMobileScreen])

  const assistantHotelDetails =
    isDetailView && detailHotel
      ? formatDrawerHotelInfo(detailHotel)
      : undefined
  const assistantHotelSearchDetails = !isDetailView
    ? buildHotelSearchDrawerContext({
        isLoading,
        hasHotels,
        searchLocation,
        checkIn,
        checkOut,
        occupancies,
        savedDrawerHotels,
        orderedHotelsWithTileDetails,
      })
    : undefined
  const assistantContextLabel = isDetailView ? detailHotel?.name : listHeaderTitle
  const assistantContextDetails = isDetailView ? assistantHotelDetails : assistantHotelSearchDetails

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      onBack={canGoBack ? handleBack : undefined}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={isDetailView ? detailHotel?.name : undefined}
          isOpen={isOpen}
          contextDetails={assistantHotelDetails}
          fallback={browseAllResultsFooter}
        />
      }
      headerActions={isDetailView ? detailHeaderActions : listHeaderActions}
      maxWidth={HOTEL_DRAWER_MAX_WIDTH_PX}
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen && !!assistantContextLabel && !!assistantContextDetails}
        contextLabel={assistantContextLabel}
        contextDetails={assistantContextDetails}
      />
      {isDetailView && (
        <>
          {isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                {shouldRenderDetailTitleInContent && selectedHotelTitle ? (
                  <Heading variant="heading3">{selectedHotelTitle}</Heading>
                ) : (
                  <LoadingBox height={28} width={240} borderRadius="S" />
                )}
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                {shouldRenderDetailTitleInContent && selectedHotelTitle && (
                  <Heading variant="heading3">{selectedHotelTitle}</Heading>
                )}
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'AyMdUB-fKPPyurSXTes2',
                    defaultMessage: 'Failed to load hotel details.',
                    description:
                      'Error message shown when hotel offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={() => refetchDetail()}
                >
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description:
                      'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailHotel && (
            <HotelDetailView
              hotel={detailHotel}
              titleInContent={shouldRenderDetailTitleInContent ? detailHotel.name : undefined}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              isBooked={isBooked}
              orderId={orderIdForLink}
              savedBookedRooms={savedBookedRooms}
              itineraryData={itineraryData}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              onAfterAction={handleClose}
              checkIn={savedBooking?.checkIn ?? checkIn}
              checkOut={savedBooking?.checkOut ?? checkOut}
              initialOccupancies={savedBooking?.occupancies ?? occupancies}
              selectedPackageId={savedBooking?.packageId}
              selectedRoomTypeId={savedBooking?.roomTypeId}
              selectedRateId={savedBooking?.rateId}
              selectedCheckIn={savedBooking?.checkIn}
              selectedCheckOut={savedBooking?.checkOut}
              selectedCharterOriginAirportCode={
                savedBooking?.charterOriginAirportCode
              }
              selectedCharterFareClass={savedBooking?.charterFareClass}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={24}>
            {shouldRenderListTitleInContent && (
              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">{listHeaderTitle}</Heading>
                <BodyText variant="large">{searchLocation}</BodyText>
              </Group>
            )}

            <ListingDrawerSearchControls
              searchLabel={intl.formatMessage({
                id: 'FuWeW8ek881Mgae0uRy4',
                defaultMessage: 'Search hotels',
                description:
                  'Label for the hotel search input in the trip planner hotel drawer',
              })}
              searchValue={searchLocation}
              searchVertical="hotel"
              searchPlaceholder={intl.formatMessage({
                id: '3ly94lLroqBfy-zQUlLB',
                defaultMessage: 'Search hotels or destinations',
                description:
                  'Placeholder for the hotel search input in the trip planner hotel drawer',
              })}
              onSearch={setSearchLocation}
              onSearchItemChange={setSelectedSearchItem}
              checkIn={checkIn}
              checkOut={checkOut}
              onDateChange={({
                checkIn: nextCheckIn,
                checkOut: nextCheckOut,
              }) => {
                setSelectedCheckIn(nextCheckIn)
                setSelectedCheckOut(nextCheckOut)
              }}
              rooms={occupancies}
              maxGuests={HOTEL_DRAWER_MAX_GUESTS}
              onRoomsChange={setOccupancies}
              disableFlexibleDates
              showCheckout
              showGuests
            />

            {hasSavedHotels && (
              <Group direction="vertical" gap={12}>
                <Heading variant="heading4">{savedHeading}</Heading>
                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    {savedDrawerHotels.map((hotel, index) => (
                      <DrawerHotelTile
                        key={hotel.id}
                        hotel={hotel}
                        isBooked={isOfferBookedInItinerary(
                          hotel.offerId,
                          itineraryData,
                        )}
                        onSelectHotel={handleSelectHotel}
                        position={index}
                        trackingOffer={trackingOfferById.get(hotel.offerId)}
                        itineraryData={itineraryData}
                        contextStop={contextStop}
                        onSendMessage={onSendMessage}
                        onAfterAction={handleClose}
                        onShowOnMap={onShowMap}
                        filterNights={filterNights}
                        roomCount={occupancies.length}
                      />
                    ))}
                  </Group>
                </OfferListEventsProvider>
              </Group>
            )}

            <Group direction="vertical" gap={16}>
              {hasSavedHotels && canShowExploreSection && (
                <Heading variant="heading4">{exploreHeading}</Heading>
              )}

              {isLoading && (
                <Group direction="vertical" gap={16}>
                  {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                    <DrawerHotelTileSkeleton key={index} />
                  ))}
                </Group>
              )}

              {!isLoading && !hasHotels && (
                <HotelListingEmptyState
                  placeName={placeName}
                  searchUrl={browseAllResultsUrl}
                />
              )}

              {!isLoading && canShowExploreSection && (
                <>
                  <OfferListEventsProvider tracking={tracking}>
                    <Group direction="vertical" gap={16}>
                      {orderedHotelsWithTileDetails.map((hotel, index) => (
                        <Fragment key={hotel.id}>
                          {hotelSearchSectionByPosition.get(index)?.type === 'nearby' && (
                            <Heading variant="heading5">
                              {nearbyOffersHeading}
                            </Heading>
                          )}
                          <DrawerHotelTile
                            hotel={hotel}
                            isBooked={isOfferBookedInItinerary(
                              hotel.offerId,
                              itineraryData,
                            )}
                            onSelectHotel={handleSelectHotel}
                            position={savedDrawerHotels.length + index}
                            trackingOffer={trackingOfferById.get(hotel.offerId)}
                            itineraryData={itineraryData}
                            contextStop={contextStop}
                            onSendMessage={onSendMessage}
                            onAfterAction={handleClose}
                            onShowOnMap={onShowMap}
                            filterNights={filterNights}
                            roomCount={occupancies.length}
                          />
                        </Fragment>
                      ))}
                    </Group>
                  </OfferListEventsProvider>

                  {isLoadingMore && (
                    <Group direction="vertical" gap={16}>
                      {Array.from({ length: 2 }, (_, index) => (
                        <DrawerHotelTileSkeleton
                          key={`loading-more-${index}`}
                        />
                      ))}
                    </Group>
                  )}

                  {hasMore && !isLoadingMore && (
                    <Group
                      ref={loadMoreTriggerRef}
                      direction="horizontal"
                      horizontalAlign="center"
                    >
                      <TextButton
                        kind="secondary"
                        size="medium"
                        onClick={loadMore}
                      >
                        {intl.formatMessage({
                          id: 'pXLqUwEQiAzarpNypTPc',
                          defaultMessage: 'Show more hotels',
                          description:
                            'Button label to load more hotel results in the listing drawer',
                        })}
                      </TextButton>
                    </Group>
                  )}
                </>
              )}
            </Group>
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default HotelListingDrawer
